import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Space,
  Switch,
  Table,
  Typography,
} from 'antd';
import { useState } from 'react';
import {
  useDeleteSubSource,
  useSaveSubSource,
  useSubSources,
} from '../../api/hooks';
import type { ProviderSubSource } from '../../api/types';
import { extractErrorMessage } from '../../api/client';

const { Text } = Typography;

/** Che API key gon cho cot hep: 4 ky tu dau + … + 4 ky tu cuoi. */
function maskKey(k?: string | null): string {
  if (!k) return '—';
  if (k.length <= 10) return k;
  return `${k.slice(0, 4)}…${k.slice(-4)}`;
}

/**
 * Tab "Nguồn con" cho provider OpenAI-compatible (vd Vilao). Mỗi nguồn con có
 * API key + Chat/Image model RIÊNG + bật/tắt. Image model dùng chung cho tạo & sửa ảnh.
 * Khi gửi request dùng
 * đúng API key + model của nguồn con. Trong Nhóm model có thể thêm cùng provider
 * nhưng khác nguồn con để xoay vòng khi lỗi.
 */
export default function SubSourcesTab({
  providerId,
  variant = 'model-source',
}: {
  providerId: string;
  variant?: 'model-source' | 'flipai';
}) {
  const { message } = AntApp.useApp();
  const subs = useSubSources(providerId);
  const save = useSaveSubSource(providerId);
  const del = useDeleteSubSource(providerId);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderSubSource | undefined>();
  const [form] = Form.useForm();
  const isFlipAi = variant === 'flipai';
  const enabledThreadTotal = (subs.data ?? []).reduce(
    (total, row) => total + (row.enabled ? Math.max(0, Number(row.maxConcurrent) || 0) : 0),
    0,
  );

  const openModal = (row?: ProviderSubSource) => {
    setEditing(row);
    form.setFieldsValue(
      row
        ? { ...row }
        : {
            name: '',
            apiKey: '',
            baseUrl: '',
            chatModel: '',
            imageModel: '',
            maxConcurrent: isFlipAi ? 1 : undefined,
            enabled: true,
            priority: 100,
          },
    );
    setOpen(true);
  };

  const onSubmit = async () => {
    const v = await form.validateFields();
    try {
      await save.mutateAsync({ subId: editing?.id, data: v });
      message.success(
        isFlipAi
          ? editing
            ? 'Đã cập nhật API con'
            : 'Đã thêm API con'
          : editing
            ? 'Đã cập nhật nguồn con'
            : 'Đã thêm nguồn con',
      );
      setOpen(false);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onToggle = async (row: ProviderSubSource, enabled: boolean) => {
    try {
      await save.mutateAsync({ subId: row.id, data: { ...row, enabled } });
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onDelete = async (id: string) => {
    try {
      await del.mutateAsync(id);
      message.success(isFlipAi ? 'Đã xoá API con' : 'Đã xoá nguồn con');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
          {isFlipAi ? 'Thêm API con' : 'Thêm nguồn con'}
        </Button>
        {isFlipAi ? (
          <>
            <Text strong>Tổng luồng đang bật: {enabledThreadTotal}</Text>
            <Text type="secondary">
              Request sẽ xoay qua các API còn luồng trống; khi dùng hết tổng số luồng, request mới sẽ chờ.
            </Text>
          </>
        ) : (
          <Text type="secondary">
            Mỗi nguồn con có Base URL + API key + model riêng. Khi nguồn lỗi, Nhóm model sẽ xoay sang nguồn con khác.
          </Text>
        )}
      </Space>

      <Table<ProviderSubSource>
        rowKey="id"
        loading={subs.isLoading}
        dataSource={subs.data ?? []}
        pagination={false}
        columns={[
          { title: 'Tên', dataIndex: 'name', render: (v) => <b>{v}</b> },
          {
            title: 'API Key',
            dataIndex: 'apiKey',
            width: 120,
            ellipsis: true,
            render: (v) => (
              <Text code style={{ fontSize: 12 }}>{maskKey(v)}</Text>
            ),
          },
          { title: 'Base URL', dataIndex: 'baseUrl', render: (v) => (v ? <Text code>{v}</Text> : '—') },
          ...(!isFlipAi
            ? [
                { title: 'Chat Model', dataIndex: 'chatModel', render: (v: string | null) => v || '—' },
                {
                  title: 'Image Model (tạo & sửa)',
                  dataIndex: 'imageModel',
                  render: (v: string | null) => v || '—',
                },
              ]
            : [
                {
                  title: 'Số luồng',
                  dataIndex: 'maxConcurrent',
                  width: 100,
                  align: 'right' as const,
                  render: (v: number | null) => <Text strong>{Number(v) || 0}</Text>,
                },
              ]),
          {
            title: 'Ghi chú',
            dataIndex: 'note',
            render: (v: string | null) =>
              v ? <Text>{v}</Text> : <Text type="secondary">—</Text>,
          },
          {
            title: 'Bật',
            dataIndex: 'enabled',
            width: 70,
            render: (v: boolean, row) => <Switch checked={v} onChange={(c) => onToggle(row, c)} />,
          },
          {
            title: '',
            width: 100,
            render: (_, row) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openModal(row)} />
                <Popconfirm
                  title={isFlipAi ? 'Xoá API con này?' : 'Xoá nguồn con này?'}
                  onConfirm={() => onDelete(row.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={
          isFlipAi
            ? editing
              ? 'Sửa API con FlipAI'
              : 'Thêm API con FlipAI'
            : editing
              ? 'Sửa nguồn con'
              : 'Thêm nguồn con'
        }
        width={640}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        confirmLoading={save.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={isFlipAi ? 'Tên API con' : 'Tên'}
            rules={[{ required: true, message: isFlipAi ? 'Nhập tên API con' : 'Nhập tên nguồn con' }]}
          >
            <Input placeholder={isFlipAi ? 'vd: Tài khoản FlipAI 01' : 'vd: gx/gpt-5.5'} />
          </Form.Item>
          <Form.Item name="apiKey" label="API Key" rules={[{ required: true, message: 'Nhập API key' }]}>
            <Input.Password placeholder={isFlipAi ? 'veo_…' : 'sk-…'} autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="baseUrl"
            label={isFlipAi ? 'Base URL (không bắt buộc)' : 'Base URL (để trống = dùng Base URL của nhà cung cấp)'}
          >
            <Input
              placeholder={isFlipAi ? 'Để trống để dùng Base URL của FlipAI' : 'vd: https://api.zonetoken.net/v1'}
              autoComplete="off"
            />
          </Form.Item>
          {!isFlipAi && (
            <>
              <Form.Item name="chatModel" label="Chat Model (/chat/completions)">
                <Input placeholder="vd: gpt-5.5" />
              </Form.Item>
              <Form.Item
                name="imageModel"
                label="Image Model (tạo & sửa ảnh)"
                tooltip="Dùng chung cho tạo ảnh và sửa ảnh: không có ảnh đính kèm = tạo ảnh, có ảnh đính kèm = sửa ảnh."
              >
                <Input placeholder="vd: gpt-5.5" />
              </Form.Item>
            </>
          )}
          {isFlipAi && (
            <Form.Item
              name="maxConcurrent"
              label="Số luồng"
              tooltip="Số request tạo ảnh/video tối đa có thể chạy đồng thời bằng API này."
              rules={[
                { required: true, message: 'Nhập số luồng của API con' },
                { type: 'number', min: 1, message: 'Số luồng phải từ 1 trở lên' },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={1} precision={0} placeholder="vd: 24" />
            </Form.Item>
          )}
          <Form.Item name="note" label="Ghi chú">
            <Input.TextArea
              rows={2}
              autoSize={{ minRows: 1, maxRows: 4 }}
              placeholder="vd: nguồn dự phòng, hết hạn 30/06, giới hạn thấp…"
            />
          </Form.Item>

          {!isFlipAi && (
            <>
              <Divider orientation="left" plain style={{ marginTop: 4 }}>
                Cấu hình vận hành (độc lập như nhà cung cấp riêng — để trống = kế thừa nhà cung cấp)
              </Divider>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="timeoutMs" label="Timeout (ms)">
                    <InputNumber style={{ width: '100%' }} min={0} placeholder="vd 120000" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="retryCount" label="Số lần tạo lại khi lỗi">
                    <InputNumber style={{ width: '100%' }} min={0} placeholder="vd 2" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="rateLimitPerMin" label="Giới hạn request/phút">
                    <InputNumber style={{ width: '100%' }} min={0} placeholder="trống/0 = không giới hạn" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="maxConcurrent" label="Số request đồng thời tối đa">
                    <InputNumber style={{ width: '100%' }} min={0} placeholder="trống/0 = không giới hạn" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Form.Item name="priority" label="Ưu tiên (nhỏ hơn = xoay trước)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="enabled" label={isFlipAi ? 'Đang sử dụng' : 'Bật'} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
